import React, { useState, useEffect, useMemo, memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Check, Play, ArrowRight, Users, Clock, Shield, Zap, ChevronDown, Star } from 'lucide-react';
import api from '../utils/api';
import { Helmet } from 'react-helmet';
import { useStructuredData } from '../hooks/useStructuredData';

// Memoized header component to prevent re-renders
const HeaderNav = memo(({ onNavigate }) => {
  return (
    <div className="absolute top-6 right-6 flex gap-3 z-10">
      <Button
        onClick={() => {
          const pricingSection = document.getElementById('pricing-section');
          if (pricingSection) {
            pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }}
        variant="outline"
        className="border-2 border-gray-300"
      >
        Pricing
      </Button>
      <Button
        onClick={() => onNavigate('/blog')}
        variant="outline"
        className="border-2 border-gray-300"
      >
        Blog
      </Button>
      <Button
        onClick={() => onNavigate('/login')}
        variant="outline"
        className="border-2 border-gray-300"
      >
        Login
      </Button>
    </div>
  );
});

HeaderNav.displayName = 'HeaderNav';

const LandingPage = () => {
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState(null);
  const [hasBlogPosts, setHasBlogPosts] = useState(true); // Default to true to prevent flash
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [heroSettings, setHeroSettings] = useState({
    main_heading: 'Professional Email Signatures',
    sub_heading: 'That Actually Work',
    description: 'Create stunning, professional email signatures for your entire team in minutes. Centralized management, consistent branding, and easy deployment across all email clients.',
    checkmarks: ['Never struggle with signatures again', 'Setup in 5 minutes', 'Cancel anytime'],
    button_text: 'Start Your Free 15-Day Trial',
    video_enabled: false,
    video_url: null
  });
  
  useStructuredData('/');

  // Memoize navigation handler
  const handleNavigation = useMemo(() => (path) => navigate(path), [navigate]);

  useEffect(() => {
    let mounted = true;

    // Check if there are any published blog posts
    const checkBlogPosts = async () => {
      try {
        const response = await api.get('/blog/posts', { params: { limit: 1 } });
        if (mounted) {
          setHasBlogPosts(response.data.total > 0);
        }
      } catch (error) {
        console.error('Failed to check blog posts:', error);
      }
    };
    
    // Fetch hero banner settings
    const fetchHeroSettings = async () => {
      try {
        const response = await api.get('/hero-banner');
        if (mounted) {
          const data = response.data || {};
          setHeroSettings((current) => ({
            ...current,
            ...data,
            checkmarks: Array.isArray(data.checkmarks) ? data.checkmarks : current.checkmarks,
          }));
        }
      } catch (error) {
        console.error('Failed to fetch hero settings:', error);
      }
    };
    
    checkBlogPosts();
    fetchHeroSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const openVideoModal = () => {
    setVideoModalOpen(true);
  };

  const closeVideoModal = () => {
    setVideoModalOpen(false);
  };

  const features = [
    { icon: '🎨', title: 'Branded Templates', description: 'Consistent branding across all signatures' },
    { icon: '🔗', title: 'Social Media Links', description: 'Add all your social profiles' },
    { icon: '⚖️', title: 'Legal Disclaimers', description: 'Built-in compliance fields' },
    { icon: '📍', title: 'Multiple Locations', description: 'Support offices worldwide' },
    { icon: '👥', title: 'Team Management', description: 'Centralized admin control' },
    { icon: '✏️', title: 'Custom Fields', description: 'Add any field you need' },
    { icon: '🏷️', title: 'White Labeling', description: 'Your brand, your way' },
    { icon: '🎛️', title: 'Control Everything', description: 'Easily toggle what\'s editable and visible for employees' },
  ];

  const emailClients = [
    { name: 'Gmail', logo: 'https://customer-assets.emergentagent.com/job_sigmailer/artifacts/n03sy7gx_gmail.png' },
    { name: 'Apple Mail', logo: 'https://customer-assets.emergentagent.com/job_sigmailer/artifacts/w0rp8ijj_apple.png' },
    { name: 'Outlook', logo: 'https://customer-assets.emergentagent.com/job_sigmailer/artifacts/uyy4kw5w_windows.png' },
  ];

  const painPoints = [
    { icon: '🎯', title: 'Inconsistent Branding', description: 'Every employee has different formatting' },
    { icon: '⏰', title: 'Time-Consuming Updates', description: 'Hours spent formatting signatures manually' },
    { icon: '🔗', title: 'Broken Links/Images', description: 'Signatures break in different email clients' },
    { icon: '⚠️', title: 'No Compliance Control', description: "Can't enforce legal disclaimers" },
  ];

  const steps = [
    { number: '1', icon: '🎨', title: 'Customize Your Template', description: 'Set your brand colors, logo, and social links' },
    { number: '2', icon: '👥', title: 'Invite Your Team', description: 'Employees create their own signatures' },
    { number: '3', icon: '🚀', title: 'Deploy Instantly', description: 'Copy-paste into email clients or use our tool' },
  ];

  const faqs = [
    {
      question: 'How long does setup take?',
      answer: 'Most companies are up and running in under 5 minutes. Simply customize your template, invite your team, and deploy.'
    },
    {
      question: 'Do I need a credit card for the trial?',
      answer: 'Yes, but you won\'t be charged until your free trial is up. Cancel before if it\'s not great, stay forever if you love it (most people do)!'
    },
    {
      question: 'What email clients are supported?',
      answer: 'Gmail, Outlook, Apple Mail, Yahoo, Thunderbird, and 20+ other email clients. Our signatures work everywhere.'
    },
    {
      question: 'Can I customize the signature?',
      answer: 'Yes! Full control over colors, logos, social links, disclaimers, and custom fields. Make it perfectly match your brand.'
    },
    {
      question: 'What happens after the trial?',
      answer: 'You can upgrade to continue using the service, or cancel anytime. No long-term commitments required.'
    },
    {
      question: 'Do you offer refunds?',
      answer: 'Yes, we offer a 30-day money-back guarantee. If you\'re not satisfied, we\'ll refund your payment, no questions asked.'
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>SlickSignature | Corporate Email Signature App for Teams</title>
        <meta name="description" content="Give your whole team standardized, on-brand email signatures in minutes. Central branding controls, multiple office locations and per-field permissions." />
        <link rel="canonical" href="https://slicksignature.com/" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="SlickSignature" />
        <meta property="og:url" content="https://slicksignature.com/" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      {/* Section 1: Hero Above the Fold */}
      <section className="relative bg-gradient-to-b from-white to-gray-50 pt-20 pb-24 px-6">
        <div className="max-w-7xl mx-auto text-center">
          {/* Logo */}
          <div className="mb-8">
            <img 
              src="https://cdn.slicksignature.com/landing/slicksignature-logo-black.png"
              alt="SlickSignature" 
              className="h-24 mx-auto"
            />
          </div>

          {/* Navigation Buttons (Top Right) - Memoized to prevent flashing */}
          <HeaderNav onNavigate={handleNavigation} />

          {/* Headline - FROM ADMIN SETTINGS */}
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            {heroSettings.main_heading}<br />
            <span className="text-[#EB2F5B]">{heroSettings.sub_heading}</span>
          </h1>

          {/* Sub-headline - FROM ADMIN SETTINGS */}
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            {heroSettings.description}
          </p>

          {/* CTA Buttons - VIDEO BUTTON FROM ADMIN SETTINGS */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            <Button 
              onClick={() => navigate('/checkout?plan=business')}
              className="bg-[#EB2F5B] hover:bg-[#d02850] text-white px-8 py-6 text-lg rounded-lg shadow-lg"
              size="lg"
            >
              {heroSettings.button_text}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            {heroSettings.video_enabled && heroSettings.video_url && (
              <Button
                onClick={openVideoModal}
                variant="outline"
                size="lg"
                className="border-2 border-gray-300 px-8 py-6 text-lg"
              >
                <Play className="mr-2 h-5 w-5" />
                Watch Demo
              </Button>
            )}
          </div>

          {/* Trust Signals - FROM ADMIN SETTINGS */}
          <div className="flex flex-wrap justify-center gap-6 text-base text-gray-600">
            {heroSettings.checkmarks.map((checkmark, index) => (
              <div key={index} className="flex items-center">
                <Check className="h-6 w-6 text-green-500 mr-3 font-bold" strokeWidth={3} />
                <span className="font-medium">{checkmark}</span>
              </div>
            ))}
          </div>

          {/* Hero Image with Subtle Background */}
          <div className="mt-16 max-w-5xl mx-auto bg-[#EFF6FF] rounded-xl p-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_sigmailer/artifacts/czbin3k4_Graphic-1.png"
              alt="SlickSignature Platform"
              className="w-full rounded-lg"
            />
          </div>
        </div>
      </section>

      {/* Section 2: Works Perfect With */}
      <section className="bg-white py-12 border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-gray-600 mb-8 text-lg font-semibold">Works Perfect With:</p>
          <div className="flex flex-wrap justify-center items-center gap-12">
            {emailClients.map((client, index) => (
              <div key={index} className="flex items-center justify-center">
                <img 
                  src={client.logo} 
                  alt={client.name}
                  className="h-16 w-auto object-contain grayscale hover:grayscale-0 transition-all"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Problem/Pain Points */}
      <section className="bg-gray-50 py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-16">
            Tired of These Email Signature Headaches?
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {painPoints.map((point, index) => (
              <Card key={index} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl mb-4">{point.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{point.title}</h3>
                  <p className="text-gray-600 text-sm">{point.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => navigate('/checkout?plan=business')}
              size="lg"
              className="bg-[#EB2F5B] hover:bg-[#d02850] text-white px-8 py-6 text-lg rounded-lg shadow-lg"
            >
              Sign Up For Free Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Section 4: Solution/Benefits */}
      <section id="solution" className="bg-white py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              One Platform. Complete Control.
            </h2>
            <p className="text-xl text-gray-600">Consistent Branding.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Image Left */}
            <div className="order-2 lg:order-1 bg-[#EFF6FF] rounded-xl p-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_sigmailer/artifacts/66uwvexo_SlickSignature-Website-Graphics-150ppi_Graphic-3-BG-2.png"
                alt="Signature Tool in Action"
                className="w-full rounded-lg"
              />
            </div>

            {/* Text Right */}
            <div className="order-1 lg:order-2 space-y-8">
              <div className="flex gap-4">
                <div className="text-3xl">🎨</div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Brand Consistency</h3>
                  <p className="text-gray-600">
                    Centralized control ensures every signature matches your brand guidelines perfectly.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="text-3xl">⚡</div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5-Minute Setup</h3>
                  <p className="text-gray-600">
                    No IT required. Get your entire team up and running instantly.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="text-3xl">📱</div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Works Everywhere</h3>
                  <p className="text-gray-600">
                    Gmail, Outlook, Apple Mail, and 20+ email clients supported.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="text-3xl">🔒</div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Compliance Ready</h3>
                  <p className="text-gray-600">
                    Built-in legal disclaimers and required fields for your industry.
                  </p>
                </div>
              </div>

              <Button 
                onClick={() => navigate('/checkout?plan=business')}
                className="bg-[#EB2F5B] hover:bg-[#d02850] text-white px-8 py-6 text-lg"
                size="lg"
              >
                {heroSettings.button_text}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: How It Works */}
      <section className="bg-gradient-to-br from-blue-50 to-indigo-50 py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-16">
            Get Started in 3 Simple Steps
          </h2>

          <div className="grid md:grid-cols-3 gap-12">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="relative mb-6">
                  <div className="w-16 h-16 bg-[#EB2F5B] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                    {step.number}
                  </div>
                  <div className="text-5xl mb-4">{step.icon}</div>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button 
              onClick={() => navigate('/checkout?plan=business')}
              className="bg-[#EB2F5B] hover:bg-[#d02850] text-white px-8 py-6 text-lg"
              size="lg"
            >
              {heroSettings.button_text}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* Section 6: Key Features Grid */}
      <section className="bg-white py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-4">
            Everything You Need. Nothing You Don't.
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16">
            All the features you need to create professional email signatures
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border border-gray-200 hover:border-[#EB2F5B] hover:shadow-lg transition-all">
                <CardContent className="p-6 text-center">
                  <div className="text-4xl mb-4">{feature.icon}</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Section 7: Testimonials */}
      <section className="bg-gray-50 py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-16">
            Loved by Marketing Teams Everywhere
          </h2>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "We saved 10+ hours per month managing signatures. The ROI was immediate and our brand looks professional across all emails."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-gray-600">
                    SJ
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Sarah Johnson</p>
                    <p className="text-sm text-gray-600">Marketing Director</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 2 */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "We saved tons of time on signature updates alone. The centralized management makes it so easy to keep everyone's info current."
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-gray-600">
                    JD
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Jessica Davis</p>
                    <p className="text-sm text-gray-600">Operations Manager</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial 3 */}
            <Card className="border-0 shadow-lg">
              <CardContent className="p-8">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-6 italic">
                  "Setup was literally 5 minutes. The whole team was up and running the same day. Best tool we've added to our stack this year!"
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-gray-600">
                    MR
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Michael Rodriguez</p>
                    <p className="text-sm text-gray-600">CEO</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Section 8: Pricing */}
      <section id="pricing-section" className="bg-white py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">Start Free. Scale as You Grow.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Personal Plan */}
            <Card className="border-2 border-gray-200 hover:border-[#EB2F5B] transition-all">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">PERSONAL</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">$9</span>
                  <span className="text-gray-600">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-gray-700 text-base">1 User</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-gray-700 text-base">All Features</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-gray-700 text-base">Email Support</span>
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/checkout?plan=personal')}
                  variant="outline"
                  className="w-full border-2 border-[#EB2F5B] text-[#EB2F5B] hover:bg-[#EB2F5B] hover:text-white"
                  size="lg"
                >
                  Start Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </CardContent>
            </Card>

            {/* Business Plan */}
            <Card className="border-2 border-[#EB2F5B] shadow-xl relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-[#EB2F5B] text-white px-4 py-1 rounded-full text-sm font-semibold">
                  ⭐ POPULAR
                </span>
              </div>
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-2">BUSINESS</h3>
                <div className="mb-6">
                  <span className="text-4xl font-bold text-gray-900">$59</span>
                  <span className="text-gray-600">/month</span>
                </div>
                <ul className="space-y-3 mb-8">
                  <li className="flex items-start">
                    <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-gray-700 font-medium text-base">Unlimited Users</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-gray-700 text-base">All Features</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-gray-700 font-medium text-base">Priority Support</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-gray-700 font-medium text-base">Team Management</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-6 w-6 text-green-500 mr-3 flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-gray-700 font-medium text-base">White Labeling</span>
                  </li>
                </ul>
                <Button 
                  onClick={() => navigate('/checkout?plan=business')}
                  className="w-full bg-[#EB2F5B] hover:bg-[#d02850] text-white"
                  size="lg"
                >
                  Start Trial
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <div className="flex flex-wrap justify-center gap-6 text-base text-gray-600">
              <div className="flex items-center">
                <Check className="h-6 w-6 text-green-500 mr-3 font-bold" strokeWidth={3} />
                <span className="font-medium">15-Day Free Trial</span>
              </div>
              <div className="flex items-center">
                <Check className="h-6 w-6 text-green-500 mr-3 font-bold" strokeWidth={3} />
                <span className="font-medium">Cancel Anytime</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 9: FAQ */}
      <section className="bg-gray-50 py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold text-center text-gray-900 mb-16">
            Frequently Asked Questions
          </h2>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <Card key={index} className="border border-gray-200 overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full text-left p-6 hover:bg-gray-50 transition-colors flex justify-between items-center"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-8">{faq.question}</h3>
                  <ChevronDown 
                    className={`h-6 w-6 text-gray-400 flex-shrink-0 transition-transform ${
                      openFaq === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600">{faq.answer}</p>
                  </div>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Section 10: Final CTA */}
      <section className="bg-gradient-to-r from-gray-900 to-black text-white py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Ready to Transform Your Email Signatures?
          </h2>
          <p className="text-xl text-gray-300 mb-10">
            Join 1,000+ companies using SlickSignature to create professional, on-brand signatures
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Button 
              onClick={() => navigate('/checkout?plan=business')}
              className="bg-[#EB2F5B] hover:bg-[#d02850] text-white px-8 py-6 text-lg"
              size="lg"
            >
              {heroSettings.button_text}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              variant="outline"
              className="border-2 border-white text-white hover:bg-white hover:text-black px-8 py-6 text-lg"
              size="lg"
              onClick={() => window.location.href = 'mailto:Hello@slicksignature.com'}
            >
              Talk to Sales
            </Button>
          </div>

          <div className="flex flex-wrap justify-center gap-6 text-base text-gray-300">
            <div className="flex items-center">
              <Check className="h-6 w-6 text-green-400 mr-3 font-bold" strokeWidth={3} />
              <span className="font-medium">Setup in 5 minutes</span>
            </div>
            <div className="flex items-center">
              <Check className="h-6 w-6 text-green-400 mr-3 font-bold" strokeWidth={3} />
              <span className="font-medium">Cancel anytime</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black text-white py-12 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Slick Signature. <a href="https://www.onlinedesignclub.com/services/website-design-development/" className="hover:text-white" target="_blank" rel="noopener noreferrer">WordPress Website Design by Online Design Club</a>.
          </p>
        </div>
      </footer>

      {/* Video Modal */}
      {videoModalOpen && heroSettings.video_url && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
          onClick={closeVideoModal}
        >
          <div 
            className="relative bg-white rounded-lg w-full max-w-4xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeVideoModal}
              className="absolute -top-10 right-0 text-white hover:text-gray-300 text-4xl font-bold"
            >
              ×
            </button>
            
            {/* Video Embed */}
            <div className="relative" style={{ paddingBottom: '56.25%' }}>
              <iframe
                className="absolute top-0 left-0 w-full h-full rounded-lg"
                src={`https://www.youtube.com/embed/${getYouTubeVideoId(heroSettings.video_url)}?autoplay=1`}
                title="Demo Video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;
